import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    TradeCancelInput,
    TradeCloseInput,
    TradeCreateInput,
    TradeListResponse,
    TradePartialCloseInput,
    TradePartialCloseResponse,
    TradeReopenInput,
    TradeResponse,
    TradeUpdateInput,
} from '@/models/trade.ts';
import httpClient from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';

// CRM Trades Hook
export const useCrmTrades = (params?: {
    status?: string;
    customerId?: string;
    per_page?: number;
    filters?: Record<string, any>;
    sort?: { key: string; direction: string };
    pagination?: { page: number; pageSize: number };
}) => {
    return useQuery<TradeListResponse>({
        queryKey: ['crm-trades', params],
        queryFn: async () => {
            const searchParams = new URLSearchParams();

            if (params) {
                // Add direct parameters first
                if (params.status) searchParams.append('status', params.status);
                if (params.customerId) searchParams.append('customer_id', params.customerId);
                if (params.per_page) searchParams.append('per_page', params.per_page.toString());

                // Add pagination
                if (params.pagination) {
                    searchParams.append('page', params.pagination.page.toString());
                    searchParams.append('per_page', params.pagination.pageSize.toString());
                }

                // Add sorting
                if (params.sort && params.sort.key) {
                    searchParams.append('sort', params.sort.key);
                    searchParams.append('direction', params.sort.direction);
                }

                // Add additional filters (excluding status and customerId only if they're provided as direct parameters)
                if (params.filters) {
                    Object.entries(params.filters).forEach(([key, value]) => {
                        if (value !== undefined && value !== null && value !== '') {
                            // Only skip if the same parameter is already provided as direct parameter
                            if (key === 'status' && params.status) {
                                // Skip - status already provided as direct parameter
                                return;
                            }
                            if (key === 'customer_id' && params.customerId) {
                                // Skip - customerId already provided as direct parameter
                                return;
                            }

                            // Add as filter_ parameter
                            searchParams.append(`filter_${key}`, value.toString());
                        }
                    });
                }
            }

            const response = await httpClient.get(
                `${API_ENDPOINTS.CRM.TRADES.LIST}?${searchParams.toString()}`
            );
            return response.data;
        },
        refetchInterval: 10000,
    });
};

// Trader Trades Hook
export const useTrades = (params?: {
    status?: string;
    per_page?: number;
    filters?: Record<string, any>;
    sort?: { key: string; direction: string };
    pagination?: { page: number; pageSize: number };
}) => {
    return useQuery<TradeListResponse>({
        queryKey: ['trader-trades', params],
        queryFn: async () => {
            const searchParams = new URLSearchParams();

            if (params) {
                // Add basic parameters
                if (params.status) searchParams.append('status', params.status);
                if (params.per_page) searchParams.append('per_page', params.per_page.toString());

                // Add pagination
                if (params.pagination) {
                    searchParams.append('page', params.pagination.page.toString());
                    searchParams.append('per_page', params.pagination.pageSize.toString());
                }

                // Add sorting
                if (params.sort && params.sort.key) {
                    searchParams.append('sort', params.sort.key);
                    searchParams.append('direction', params.sort.direction);
                }

                // Add additional filters (excluding status only if it's provided as direct parameter)
                if (params.filters) {
                    Object.entries(params.filters).forEach(([key, value]) => {
                        if (value !== undefined && value !== null && value !== '') {
                            // Only skip if the same parameter is already provided as direct parameter
                            if (key === 'status' && params.status) {
                                // Skip - status already provided as direct parameter
                                return;
                            }

                            // Special handling for customer_id - send as direct parameter
                            if (key === 'customer_id') {
                                searchParams.append('customer_id', value.toString());
                            } else {
                                // Add as filter_ parameter
                                searchParams.append(`filter_${key}`, value.toString());
                            }
                        }
                    });
                }
            }

            const response = await httpClient.get(
                `${API_ENDPOINTS.TRADER.TRADES.LIST}?${searchParams.toString()}`
            );
            return response.data;
        },
        // No polling — invalidated by useTradesSocket on trade/order events
    });
};

// Individual Trade Hook
export const useTrade = (id: string) => {
    return useQuery<TradeResponse>({
        queryKey: ['trade', id],
        queryFn: async () => {
            const response = await httpClient.get(API_ENDPOINTS.TRADER.TRADES.DETAILS(id));
            return response.data;
        },
        // No polling — invalidated by useTradesSocket on trade/order events
        enabled: !!id,
    });
};

// CRM Individual Trade Hook
export const useCrmTrade = (id: string) => {
    return useQuery<TradeResponse>({
        queryKey: ['crm-trade', id],
        queryFn: async () => {
            const response = await httpClient.get(API_ENDPOINTS.CRM.TRADES.DETAILS(id));
            return response.data;
        },
        refetchInterval: 10000,
        enabled: !!id,
    });
};

// Create Trade Mutation
export const useCreateTrade = () => {
    const queryClient = useQueryClient();

    return useMutation<TradeResponse, Error, TradeCreateInput>({
        mutationFn: async (data: TradeCreateInput) => {
            const response = await httpClient.post('trader/trades', data);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate all trader trade queries to refresh all trade lists
            queryClient.invalidateQueries({ queryKey: ['trader-trades'] });
            // Also invalidate any trade summary queries
            queryClient.invalidateQueries({ queryKey: ['trader-trades-summary'] });
        },
    });
};

// CRM Create Trade Mutation
export const useCrmCreateTrade = () => {
    const queryClient = useQueryClient();

    return useMutation<TradeResponse, Error, TradeCreateInput & { customer_id: string }>({
        mutationFn: async (data: TradeCreateInput & { customer_id: string }) => {
            const response = await httpClient.post('crm/trades', data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['crm-trades'] });
        },
    });
};

// Update Trade Mutation
export const useUpdateTrade = () => {
    const queryClient = useQueryClient();

    return useMutation<TradeResponse, Error, { id: string; data: TradeUpdateInput }>({
        mutationFn: async ({ id, data }) => {
            const response = await httpClient.put(API_ENDPOINTS.TRADER.TRADES.DETAILS(id), data);
            return response.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['trade', id] });
            queryClient.invalidateQueries({ queryKey: ['trader-trades'] });
        },
    });
};

// CRM Update Trade Mutation
export const useCrmUpdateTrade = () => {
    const queryClient = useQueryClient();

    return useMutation<TradeResponse, Error, { id: string; data: TradeUpdateInput }>({
        mutationFn: async ({ id, data }) => {
            const response = await httpClient.put(API_ENDPOINTS.CRM.TRADES.UPDATE(id), data);
            return response.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['crm-trade', id] });
            queryClient.invalidateQueries({ queryKey: ['crm-trades'] });
        },
    });
};

// Cancel Trade Mutation
export const useCancelTrade = () => {
    const queryClient = useQueryClient();

    return useMutation<TradeResponse, Error, { id: string; data?: TradeCancelInput }>({
        mutationFn: async ({ id, data }) => {
            const response = await httpClient.post(
                API_ENDPOINTS.TRADER.ORDERS.CANCEL(id),
                data || {}
            );
            return response.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['trade', id] });
            queryClient.invalidateQueries({ queryKey: ['trader-trades'] });
        },
    });
};

// CRM Cancel Trade Mutation
export const useCrmCancelTrade = () => {
    const queryClient = useQueryClient();

    return useMutation<TradeResponse, Error, { id: string; data?: TradeCancelInput }>({
        mutationFn: async ({ id, data }) => {
            const response = await httpClient.post(API_ENDPOINTS.CRM.ORDERS.CANCEL(id), data || {});
            return response.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['crm-trade', id] });
            queryClient.invalidateQueries({ queryKey: ['crm-trades'] });
        },
    });
};

// Close Trade Mutation
export const useCloseTrade = () => {
    const queryClient = useQueryClient();

    return useMutation<TradeResponse, Error, { id: string; data: TradeCloseInput }>({
        mutationFn: async ({ id, data }) => {
            const response = await httpClient.post(API_ENDPOINTS.TRADER.ORDERS.CLOSE(id), data);
            return response.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['trade', id] });
            queryClient.invalidateQueries({ queryKey: ['trader-trades'] });
        },
    });
};

// CRM Close Trade Mutation
export const useCrmCloseTrade = () => {
    const queryClient = useQueryClient();

    return useMutation<TradeResponse, Error, { id: string; data: TradeCloseInput }>({
        mutationFn: async ({ id, data }) => {
            const response = await httpClient.post(API_ENDPOINTS.CRM.ORDERS.CLOSE(id), data);
            return response.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['crm-trade', id] });
            queryClient.invalidateQueries({ queryKey: ['crm-trades'] });
        },
    });
};

// Partial Close Trade Mutation
export const usePartialCloseTrade = () => {
    const queryClient = useQueryClient();

    return useMutation<
        TradePartialCloseResponse,
        Error,
        { id: string; data: TradePartialCloseInput }
    >({
        mutationFn: async ({ id, data }) => {
            const response = await httpClient.post(`trader/trades/${id}/partial-close`, data);
            return response.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['trade', id] });
            queryClient.invalidateQueries({ queryKey: ['trader-trades'] });
        },
    });
};

// CRM Partial Close Trade Mutation
export const useCrmPartialCloseTrade = () => {
    const queryClient = useQueryClient();

    return useMutation<
        TradePartialCloseResponse,
        Error,
        { id: string; data: TradePartialCloseInput }
    >({
        mutationFn: async ({ id, data }) => {
            const response = await httpClient.post(
                API_ENDPOINTS.CRM.ORDERS.PARTIAL_CLOSE(id),
                data
            );
            return response.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['crm-trade', id] });
            queryClient.invalidateQueries({ queryKey: ['crm-trades'] });
        },
    });
};

// Reopen Trade Mutation (CRM only)
export const useCrmReopenTrade = () => {
    const queryClient = useQueryClient();

    return useMutation<TradeResponse, Error, { id: string; data: TradeReopenInput }>({
        mutationFn: async ({ id, data }) => {
            const response = await httpClient.post(`crm/trades/${id}/reopen`, data);
            return response.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['crm-trade', id] });
            queryClient.invalidateQueries({ queryKey: ['crm-trades'] });
        },
    });
};
